import { useState, useEffect } from 'react';

import { Certificate } from '@revenuemarket/bank';
import { Button, Colors, Flex, Text, Input as CDSInput, Dropdown } from '@revenuemarket/cds';
import { ResponseStatus } from '@revenuemarket/http';
import { Toast } from '@revenuemarket/ui';
import { formatCompanyTaxNumber, deformatCompanyTaxNumber } from '@revenuemarket/utils';

import { BusinessRegistrationNumberInfo } from '~apis/bank/getBusinessRegistrationNumbers';
import CommonModal from '~components/ui/Modal/CommonModal';
import { useBusinessRegistrationNumbers } from '~hooks/queries/useBusinessRegistrationNumbers';
import useUpdateCertificateBusinessRegNumberMutation from '~hooks/queries/useUpdateCertificateBusinessRegNumberMutation';
import { useCompanyId } from '~hooks/useCompanyId';
import { validateBusinessRegNumber } from '~utils/businessRegNumber/validator';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  certificate: Certificate;
}

const CertificateEditModal: React.FC<Props> = ({ isOpen, onClose, certificate }) => {
  const { companyId } = useCompanyId();
  const [businessRegNumber, setBusinessRegNumber] = useState('');
  const [businessNumberError, setBusinessNumberError] = useState<string | undefined>(undefined);

  const { mutateAsync: updateBusinessRegNumber, isLoading: isUpdating } =
    useUpdateCertificateBusinessRegNumberMutation();
  const { data: businessNumbersData } = useBusinessRegistrationNumbers();

  // 이전 값으로 초기화
  useEffect(() => {
    if (certificate.businessRegNumber) {
      setBusinessRegNumber(certificate.businessRegNumber);
    } else {
      setBusinessRegNumber('');
      setBusinessNumberError(undefined);
    }
  }, [certificate.businessRegNumber]);

  const handleBusinessNumberChange = (value: string) => {
    setBusinessRegNumber(value);

    if (value.length > 0) {
      const validation = validateBusinessRegNumber(value);
      setBusinessNumberError(validation.error);
    } else {
      setBusinessNumberError(undefined);
    }
  };

  const handleBusinessNumberSelect = (value: string) => {
    setBusinessRegNumber(value);
    const validation = validateBusinessRegNumber(value);
    setBusinessNumberError(validation.error);
  };

  const handleSubmit = async () => {
    try {
      const numbers = businessRegNumber.replace(/[^\d]/g, '');

      const response = await updateBusinessRegNumber({
        companyId,
        certificateId: certificate.certificateId,
        businessRegNumber: numbers,
      });

      if (response?.Status === ResponseStatus.SUCCESS) {
        Toast.success('사업자등록번호가 수정되었습니다.');
        onClose();
      } else {
        Toast.error('사업자등록번호 수정에 실패했습니다.');
      }
    } catch (error) {
      Toast.error('사업자등록번호 수정에 실패했습니다.');
    }
  };

  const isValid = validateBusinessRegNumber(businessRegNumber).isValid;

  return (
    <CommonModal
      width={400}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      closable={false}
      maskClosable={false}
      title={
        <Flex justify="space-between" align="center" style={{ width: '100%' }}>
          <Text textStyle="16_600" style={{ color: Colors.gray900 }}>
            공동인증서 정보 수정
          </Text>
          <Button
            onClick={onClose}
            border
            buttonType="weak"
            buttonSize="small"
            style={{ width: 32, height: 32, padding: 0 }}
            disabled={isUpdating}
          >
            ✕
          </Button>
        </Flex>
      }
    >
      <Flex direction="column" gap={8} style={{ width: '100%' }}>
        <Flex
          direction="column"
          gap={2}
          style={{
            backgroundColor: Colors.gray30,
            padding: 16,
            borderRadius: 16,
            width: '100%',
          }}
        >
          <Text
            textStyle="13_500"
            style={{
              color: Colors.gray700,
              wordBreak: 'break-all',
            }}
          >
            {certificate.certificateOwner}
          </Text>
          <Text textStyle="13_400" style={{ color: Colors.gray600 }}>
            {certificate.certificateAuthority || '금융결제원'} ·{' '}
            {certificate.certificateType || '전자세금용'}
          </Text>
          <Text textStyle="13_400" style={{ color: Colors.gray600 }}>
            만료일 {certificate.certificateExpireDate}
          </Text>
        </Flex>

        <Flex direction="column" gap={8} style={{ width: '100%', paddingTop: 8 }}>
          <Text textStyle="14_400" style={{ color: 'rgba(0,0,0,0.88)' }}>
            사업자등록번호
          </Text>
          <Dropdown
            menu={{
              items:
                businessNumbersData?.Data?.data?.businessRegNumbers?.map(
                  (item: BusinessRegistrationNumberInfo) => ({
                    key: item.businessRegNumber,
                    label: item.companyName
                      ? `${formatCompanyTaxNumber(item.businessRegNumber)} (${item.companyName})`
                      : formatCompanyTaxNumber(item.businessRegNumber),
                    onClick: () => handleBusinessNumberSelect(item.businessRegNumber),
                  }),
                ) || [],
            }}
            openTrigger="click"
          >
            <CDSInput
              value={businessRegNumber}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length > 10) {
                  return;
                }
                handleBusinessNumberChange(value);
              }}
              formatter={formatCompanyTaxNumber}
              deformatter={deformatCompanyTaxNumber}
              inputSize="default"
              inputStatus={businessNumberError ? 'error' : undefined}
              additionalText={businessNumberError}
              placeholder="사업자등록번호 10자리"
            />
          </Dropdown>
        </Flex>

        <Flex justify="flex-end" gap={8} style={{ paddingTop: 8 }}>
          <Button
            onClick={onClose}
            border
            buttonType="weak"
            style={{ width: 60 }}
            disabled={isUpdating}
          >
            닫기
          </Button>
          <Button
            onClick={handleSubmit}
            buttonType="primary"
            style={{ width: 60 }}
            disabled={!isValid || isUpdating}
            loading={isUpdating}
          >
            적용
          </Button>
        </Flex>
      </Flex>
    </CommonModal>
  );
};

export default CertificateEditModal;
